/** Closed validation of the wardrobe envelope, config and adapted appearance (batch-003 `validate.ts`). */
import { keys, one, range, record, validateRecipe as validateStaticRecipe, validateTokens } from '../../../shared/assets/hushStatic/validation';
import type { HushAppearance, HushWardrobeConfig, WardrobeRecipe } from './types';
import { REVISION } from './types';

export function validateWardrobeRecipe(raw: unknown): WardrobeRecipe {
  const r = record(raw, 'wardrobe recipe');
  keys(r, ['schema', 'id', 'assetId', 'generatorId', 'seed', 'paletteId', 'detail', 'params'], 'wardrobe recipe');
  one(r.schema, ['hush.asset-recipe.v1'], 'schema');
  one(r.generatorId, ['hush.ghost_wardrobe.v1'], 'generatorId');
  if (typeof r.id !== 'string' || !/^[a-z0-9_.-]{1,120}$/.test(r.id)) throw new Error('invalid recipe id');
  range(r.seed, 0, 0xffffffff, 'seed', true);
  one(r.paletteId, ['coastal_salt', 'nook_warm', 'icon_ink'], 'palette');
  one(r.detail, ['readable', 'standard', 'close'], 'detail');
  const p = record(r.params, 'wardrobe params');
  keys(p, ['look', 'pose', 'propSupport', 'facePolicy'], 'wardrobe params');
  one(p.look, ['pip', 'mallow'], 'look');
  one(p.pose, ['idle', 'talk', 'gesture'], 'pose');
  one(p.propSupport, ['rig-if-available-else-nearby'], 'propSupport');
  one(p.facePolicy, ['preserve-existing-rig-face'], 'facePolicy');
  one(r.assetId, [p.look === 'pip' ? 'npc.pip_reedwhistle.appearance' : 'npc.mallow_wick.appearance'], 'assetId/look');
  return raw as WardrobeRecipe;
}

export function validateConfig(raw: unknown): HushWardrobeConfig {
  const c = record(raw, 'wardrobe config');
  keys(c, ['schema', 'revision', 'recipe', 'tokens', 'resources'], 'wardrobe config');
  one(c.schema, ['hush.rig-accessory.v1'], 'config schema');
  one(c.revision, [REVISION], 'revision');
  validateWardrobeRecipe(c.recipe);
  validateTokens(c.tokens);
  const r = record(c.resources, 'resources');
  keys(r, ['ribbon', 'boat'], 'resources');
  const ribbon = validateStaticRecipe(r.ribbon);
  const boat = validateStaticRecipe(r.boat);
  one(ribbon.generatorId, ['hush.ribbon.v1'], 'ribbon resource');
  one(boat.generatorId, ['hush.paper_boat.v1'], 'boat resource');
  return raw as HushWardrobeConfig;
}

export function validateAppearance(a: unknown): asserts a is HushAppearance {
  const p = record(a, 'appearance');
  one(p.shape, ['classic', 'slim', 'round', 'wide'], 'body shape');
  one(p.bodySize ?? 'normal', ['small', 'normal', 'large'], 'body size');
  if (typeof p.color !== 'string' || !/^#[a-f0-9]{6}$/i.test(p.color)) throw new Error('invalid body material');
  if (p.eyes !== 'round' || !['smile', 'o'].includes(p.mouth as string)) {
    throw new Error('friendly face policy requires existing round eyes and smile/talk mouth');
  }
  const extras = record(p.humanoid, 'humanoid');
  if (extras.fit !== true || !extras.art) throw new Error('fitted existing art rig required');
  validateConfig(extras.hushWardrobe);
}
